// Packages
import Vue from 'vue';
import debounce from 'lodash/debounce';

// Swiper
import Swiper from 'swiper';
import { Autoplay, EffectFade, Navigation as SwiperNavigation, Pagination as SwiperPagination } from 'swiper/modules';
Swiper.use([Autoplay, EffectFade, SwiperNavigation, SwiperPagination]);

// Core
import { scrollAnimationProgress } from '~scripts/core/animations';
import { BREAKPOINTS } from '~scripts/core/breakpoints';
import { isInViewport } from '~scripts/core/helpers';

// Blocks
import QuestionnaireVideo from './vue/QuestionnaireVideo.vue';

// Breakpoints
const mediaSm = window.matchMedia(`(max-width: ${BREAKPOINTS.smMax})`);

//
// QUESTIONNAIRE
//
function questionnaireInit() {
  if (region !== 'ai') {
    new Vue({
      el: '.js-questionnaire',
      name: 'QuestionnaireVideo',
      components: {
        QuestionnaireVideo,
      },
    });

    const questionnaire = document.querySelector('.js-questionnaire');

    //
    // Tabs
    //
    const questionnaireTabs = document.querySelector('.js-questionnaire-tabs');
    const questionnaireTabsItems = document.querySelectorAll('.js-questionnaire-tabs__item');

    let questionnaireTabsLeftOffsets = [];

    const fillTabs = (index) => {
      questionnaireTabsItems.forEach((element) => {
        element.classList.remove('questionnaire-tabs__item--fill');
      });

      questionnaireTabsItems[index].classList.add('questionnaire-tabs__item--fill');
    };

    const centerTabsItem = (element = questionnaireTabsItems[0]) => {
      const elementWidth = element.offsetWidth;
      const windowWidth = window.innerWidth;
      const itemIndex = Array.from(questionnaireTabsItems).indexOf(element);

      if (questionnaireTabsLeftOffsets.length === 0) {
        questionnaireTabsLeftOffsets = Array.from(questionnaireTabsItems).map(
          (element) => element.getBoundingClientRect().left
        );
      }

      const marginLeft = windowWidth / 2 - questionnaireTabsLeftOffsets[itemIndex] - elementWidth / 2;
      questionnaireTabs.style.transform = `translateX(${marginLeft}px)`;
    };

    const handleTabClick = (element, index) => {
      const speed = mediaSm.matches ? 800 : 0;

      centerTabsItem(element);
      fillTabs(index);
      questionnaireVideoSlider.slideTo(index, speed, false);
    };

    questionnaireTabsItems.forEach((element, index) => {
      element.addEventListener('click', () => {
        handleTabClick(element, index);
      });
    });

    const debouncedCenterTabsItem = debounce(() => {
      centerTabsItem(questionnaireTabsItems[questionnaireVideoSlider.activeIndex] || questionnaireTabsItems[0]);
    }, 250);

    debouncedCenterTabsItem();
    window.addEventListener('resize', debouncedCenterTabsItem);

    //
    // Video slider
    //
    const questionnaireVideoSliderNavButtons = document.querySelectorAll('.js-swiper-nav');

    questionnaireVideoSliderNavButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const isNextButtonDisabled =
          button.classList.contains('swiper-button-next') && button.classList.contains('swiper-button--disabled');
        const isPrevButtonDisabled =
          button.classList.contains('swiper-button-prev') && button.classList.contains('swiper-button--disabled');

        if (isNextButtonDisabled) {
          setTimeout(() => {
            questionnaireVideoSlider.slideTo(0, 0, false);
          }, 10);
        }

        if (isPrevButtonDisabled) {
          setTimeout(() => {
            questionnaireVideoSlider.slideTo(questionnaireVideoSlider.slides.length, 0, false);
          }, 10);
        }
      });
    });

    const questionnaireVideoSliderOptionsSm = {
      slidesPerView: 'auto',
      speed: 600,
      spaceBetween: 12,
      pagination: {
        el: '.swiper-pagination',
        type: 'bullets',
        clickable: true,
      },
    };

    const questionnaireVideoSliderOptionsMd = {
      init: false,
      slidesPerView: 1,
      effect: 'fade',
      simulateTouch: false,
      speed: 0,
      fadeEffect: {
        crossFade: true,
      },
      autoplay: {
        disableOnInteraction: false,
        waitForTransition: false,
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
        disabledClass: 'swiper-button--disabled',
      },
      on: {
        init() {
          setTimeout(() => {
            const currentVideo = this.slides[0].querySelector('.questionnaire-slider__video');
            document.querySelector('#questionnaireVideoSlider').classList.add('questionnaire-slider--show');
            currentVideo.currentTime = 0;
            currentVideo.play();
            questionnaireTabsItems[0].classList.add('questionnaire-tabs__item--fill');
          }, 5);
        },
        slideChange() {
          setTimeout(() => {
            const { previousIndex, activeIndex } = this;
            const prevVideo = this.slides[previousIndex].querySelector('.questionnaire-slider__video');
            const currentVideo = this.slides[activeIndex].querySelector('.questionnaire-slider__video');

            currentVideo.play();
            prevVideo.pause();
            setTimeout(() => {
              prevVideo.currentTime = 0;
            }, 600);

            centerTabsItem(questionnaireTabsItems[activeIndex]);
            fillTabs(activeIndex);
          }, 5);
        },
      },
    };

    let questionnaireVideoSlider = null;
    let isSliderInitialized = false;

    const handleQuestionnaireVideoSliderInit = () => {
      if (questionnaireVideoSlider && questionnaireVideoSlider.initialized) {
        questionnaireVideoSlider.destroy();
      }

      const options = mediaSm.matches ? questionnaireVideoSliderOptionsSm : questionnaireVideoSliderOptionsMd;

      questionnaireVideoSlider = new Swiper('#questionnaireVideoSlider', { ...options });

      if (isSliderInitialized) {
        questionnaireVideoSlider.init();
      }
    };

    handleQuestionnaireVideoSliderInit();
    mediaSm.addEventListener('change', handleQuestionnaireVideoSliderInit);

    // eslint-disable-next-line no-unused-vars
    let stopQuestionnaireImgAnimation;

    const handleQuestionnaireImgAnimation = () => {
      const imgContainer = document.querySelector('.js-questionnaire-image-container');

      if (mediaSm.matches) {
        stopQuestionnaireImgAnimation = null;
      } else {
        stopQuestionnaireImgAnimation = scrollAnimationProgress(imgContainer, {
          getStart(el) {
            const rect = el.getBoundingClientRect();
            return rect.top + window.scrollY - window.innerHeight;
          },
          getEnd(el, start) {
            return start + 270;
          },
          onProgress: (progress) => {
            let isAnimated = false;
            if (progress > 0.99) {
              questionnaireTabs.classList.remove('questionnaire-tabs--disabled');
              imgContainer.classList.add('questionnaire-image-container--fade');
              isAnimated = true;
            }
            if (!isSliderInitialized && isAnimated) {
              questionnaireVideoSlider.init();
              isSliderInitialized = true;
            }
          },
        });
      }
    };

    handleQuestionnaireImgAnimation();
    mediaSm.addEventListener('change', handleQuestionnaireImgAnimation);

    let changeSliderPosition = false;
    const questionnaireWrapper = document.querySelector('.js-questionnaire-slider-wrapper');

    document.addEventListener('scroll', () => {
      if (
        isInViewport(questionnaireWrapper, true, questionnaireWrapper.innerHeight) &&
        changeSliderPosition &&
        isSliderInitialized
      ) {
        questionnaireVideoSlider.slideTo(0, 0, false);
        changeSliderPosition = false;
      }

      if (!isInViewport(questionnaire) && questionnaireVideoSlider.initialized) {
        changeSliderPosition = true;
      }
    });
  }
}

export default questionnaireInit;
